import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  Divider,
  Chip,
  Alert,
} from '@mui/material';
import {
  Person as PersonIcon,
  Language as LanguageIcon,
  Psychology as PsychologyIcon,
  VolumeUp as VolumeUpIcon,
  TextFields as TextFieldsIcon,
  VideoLibrary as VideoLibraryIcon,
} from '@mui/icons-material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { User } from '../types/api';
import { usersApi } from '../services/api';

interface UserDetailsModalProps {
  user: User | null;
  open: boolean;
  onClose: () => void;
  readOnly?: boolean;
}

export default function UserDetailsModal({ user, open, onClose, readOnly = false }: UserDetailsModalProps) {
  const [editedUser, setEditedUser] = useState<Partial<User>>({});
  const [editMode, setEditMode] = useState(false);

  const queryClient = useQueryClient();

  // Мутация для обновления пользователя
  const updateMutation = useMutation({
    mutationFn: (userData: any) => usersApi.updateUser(user!.id, userData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setEditMode(false);
      onClose();
    },
  });

  React.useEffect(() => {
    if (user) {
      setEditedUser(user);
    }
  }, [user]);

  if (!user) return null;

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'Не указано';
    return new Date(dateString).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleSave = () => {
    const updates = {
      username: editedUser.username,
      first_name: editedUser.first_name,
      last_name: editedUser.last_name,
      user_language: editedUser.user_language,
      llm_model: editedUser.llm_model,
      specify_audio_language: editedUser.specify_audio_language,
      download_video: editedUser.download_video,
      transcription_format: editedUser.transcription_format,
      is_bot_blocked: editedUser.is_bot_blocked,
      // Подписка
      subscription: editedUser.subscription,
      subscription_type: editedUser.subscription_type,
      subscription_id: editedUser.subscription_id,
      subscription_autopay: editedUser.subscription_autopay,
      start_date: editedUser.start_date,
      end_date: editedUser.end_date,
      audio_uses: editedUser.audio_uses,
      gpt_uses: editedUser.gpt_uses,
    };
    
    updateMutation.mutate(updates);
  };

  const handleFieldChange = (field: keyof User, value: any) => {
    setEditedUser(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon />
          <Typography variant="h6">
            Пользователь #{user.id}
          </Typography>
          {user.is_bot_blocked && (
            <Chip label="Заблокирован" color="error" size="small" />
          )}
        </Box>
      </DialogTitle>

      <DialogContent>
        {updateMutation.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Ошибка сохранения: {(updateMutation.error as Error).message}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Основная информация */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon fontSize="small" />
              Основная информация
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Telegram ID"
              value={user.telegram_id}
              disabled
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Username"
              value={editMode ? editedUser.username || '' : user.username || 'Не указано'}
              disabled={!editMode}
              onChange={(e) => handleFieldChange('username', e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Имя"
              value={editMode ? editedUser.first_name || '' : user.first_name || 'Не указано'}
              disabled={!editMode}
              onChange={(e) => handleFieldChange('first_name', e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Фамилия"
              value={editMode ? editedUser.last_name || '' : user.last_name || 'Не указано'}
              disabled={!editMode}
              onChange={(e) => handleFieldChange('last_name', e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LanguageIcon fontSize="small" />
              Настройки
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth disabled={!editMode}>
              <InputLabel>Язык интерфейса</InputLabel>
              <Select
                value={editMode ? editedUser.user_language || 'ru' : user.user_language}
                label="Язык интерфейса"
                onChange={(e) => handleFieldChange('user_language', e.target.value)}
              >
                <MenuItem value="ru">Русский</MenuItem>
                <MenuItem value="en">English</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth disabled={!editMode}>
              <InputLabel>LLM модель</InputLabel>
              <Select
                value={editMode ? editedUser.llm_model || 'ChatGPT' : user.llm_model || 'ChatGPT'}
                label="LLM модель"
                onChange={(e) => handleFieldChange('llm_model', e.target.value)}
              >
                <MenuItem value="Claude">Claude</MenuItem>
                <MenuItem value="ChatGPT">ChatGPT</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth disabled={!editMode}>
              <InputLabel>Формат транскрипции</InputLabel>
              <Select
                value={editMode ? editedUser.transcription_format || 'docx' : user.transcription_format || 'docx'}
                label="Формат транскрипции"
                onChange={(e) => handleFieldChange('transcription_format', e.target.value)}
              >
                <MenuItem value="google_docs">Google Docs</MenuItem>
                <MenuItem value="docx">DOCX</MenuItem>
                <MenuItem value="pdf">PDF</MenuItem>
                <MenuItem value="txt">TXT</MenuItem>
                <MenuItem value="md">Markdown</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={{ pt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editMode ? editedUser.specify_audio_language || false : user.specify_audio_language}
                    disabled={!editMode}
                    onChange={(e) => handleFieldChange('specify_audio_language', e.target.checked)}
                  />
                }
                label="Указывать язык аудио"
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={{ pt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editMode ? editedUser.download_video !== false : user.download_video}
                    disabled={!editMode}
                    onChange={(e) => handleFieldChange('download_video', e.target.checked)}
                  />
                }
                label="Скачивать видео"
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={{ pt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editMode ? editedUser.is_bot_blocked || false : user.is_bot_blocked}
                    disabled={!editMode}
                    onChange={(e) => handleFieldChange('is_bot_blocked', e.target.checked)}
                  />
                }
                label="Заблокирован"
              />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              Статистика использования
            </Typography>
          </Grid>

          <Grid item xs={12} sm={3}>
            {editMode ? (
              <TextField
                fullWidth
                label="Аудио запросов"
                type="number"
                value={editedUser.audio_uses || 0}
                onChange={(e) => handleFieldChange('audio_uses', parseInt(e.target.value) || 0)}
                InputProps={{ inputProps: { min: 0 } }}
              />
            ) : (
              <Box sx={{ textAlign: 'center', p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                <VolumeUpIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h5" fontWeight={600}>
                  {user.audio_uses}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Аудио запросов
                </Typography>
              </Box>
            )}
          </Grid>

          <Grid item xs={12} sm={3}>
            {editMode ? (
              <TextField
                fullWidth
                label="GPT запросов"
                type="number"
                value={editedUser.gpt_uses || 0}
                onChange={(e) => handleFieldChange('gpt_uses', parseInt(e.target.value) || 0)}
                InputProps={{ inputProps: { min: 0 } }}
              />
            ) : (
              <Box sx={{ textAlign: 'center', p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                <PsychologyIcon color="secondary" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h5" fontWeight={600}>
                  {user.gpt_uses}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  GPT запросов
                </Typography>
              </Box>
            )}
          </Grid>

          <Grid item xs={12} sm={3}>
            <Box sx={{ textAlign: 'center', p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Регистрация
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {formatDate(user.created_at)}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={3}>
            <Box sx={{ textAlign: 'center', p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Подписка до
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {formatDate(user.end_date)}
              </Typography>
            </Box>
          </Grid>

          {/* Подписка */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              Информация о подписке
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth disabled={!editMode}>
              <InputLabel>Статус подписки</InputLabel>
              <Select
                value={editMode ? editedUser.subscription || 'False' : user.subscription}
                label="Статус подписки"
                onChange={(e) => handleFieldChange('subscription', e.target.value)}
              >
                <MenuItem value="False">False (нет)</MenuItem>
                <MenuItem value="True">True (есть)</MenuItem>
                
                
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth disabled={!editMode}>
              <InputLabel>Тип подписки</InputLabel>
              <Select
                value={editMode ? editedUser.subscription_type || '' : user.subscription_type || ''}
                label="Тип подписки"
                onChange={(e) => handleFieldChange('subscription_type', e.target.value)}
              >
                <MenuItem value="">Не указано</MenuItem>
                <MenuItem value="Недельная">Недельная</MenuItem>
                <MenuItem value="Месячная">Месячная</MenuItem>
                <MenuItem value="manual">Ручная (manual)</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="ID подписки"
              value={editMode ? editedUser.subscription_id || '' : user.subscription_id || ''}
              disabled={!editMode}
              onChange={(e) => handleFieldChange('subscription_id', e.target.value)}
              placeholder="Введите ID подписки"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={{ pt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editMode ? editedUser.subscription_autopay || false : user.subscription_autopay}
                    disabled={!editMode}
                    onChange={(e) => handleFieldChange('subscription_autopay', e.target.checked)}
                  />
                }
                label="Автоплатеж"
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Начало подписки"
              type={editMode ? "datetime-local" : "text"}
              value={editMode 
                ? (editedUser.start_date ? new Date(editedUser.start_date).toISOString().slice(0, 16) : '')
                : formatDate(user.start_date)
              }
              disabled={!editMode}
              onChange={(e) => {
                if (e.target.value) {
                  handleFieldChange('start_date', new Date(e.target.value).toISOString());
                }
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Окончание подписки"
              type={editMode ? "datetime-local" : "text"}
              value={editMode 
                ? (editedUser.end_date ? new Date(editedUser.end_date).toISOString().slice(0, 16) : '')
                : formatDate(user.end_date)
              }
              disabled={!editMode}
              onChange={(e) => {
                if (e.target.value) {
                  handleFieldChange('end_date', new Date(e.target.value).toISOString());
                }
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">
          {editMode ? 'Отмена' : 'Закрыть'}
        </Button>
        
        {!readOnly && (
          editMode ? (
            <>
              <Button 
                onClick={() => setEditMode(false)} 
                color="inherit"
                disabled={updateMutation.isPending}
              >
                Отменить изменения
              </Button>
              <Button
                onClick={handleSave}
                variant="contained"
                disabled={updateMutation.isPending}
              >
                {updateMutation.isPending ? 'Сохранение...' : 'Сохранить'}
              </Button>
            </>
          ) : (
            <Button onClick={() => setEditMode(true)} variant="contained">
              Редактировать
            </Button>
          )
        )}
      </DialogActions>
    </Dialog>
  );
}